import React, { useEffect, useMemo, useRef } from "react"
import { GlobalSidebar, SidebarList, FormInputAntd, AntdButton } from "components"
import { yupResolver } from "@hookform/resolvers/yup"
import { SDCountiesInterface } from "@type/staticData.types"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { SDCountiesInitialValues, SDCountiesSchema } from "Schema/StaticData/SDCounties"
import {
	getSDCounties,
	isSDCountiesLoading,
	isSDCountiesSaving,
	saveSDCounties,
	selectSDCountiesById,
	selectSDCountiesList,
} from "store/StaticData/SDCounties.slice"
import { useAppDispatch, useAppSelector } from "hooks"
import { useAdminQueryParams } from "hooks"
import { sortByProperty } from "config/utils"

interface EquipmentTypeFormHandler extends SDCountiesInterface {}

const SDCounties = () => {
	const dispatch = useAppDispatch()
	const saving = useAppSelector(isSDCountiesSaving())
	const loading = useAppSelector(isSDCountiesLoading())
	const SDCountiessList = useAppSelector(selectSDCountiesList)
	const { params, setSearchParams } = useAdminQueryParams()
	const { selected: selectedSDCounty = "" } = params
	const SDCounties = useAppSelector(selectSDCountiesById(selectedSDCounty))
	const isNewBeingCreated = useRef(false)
	const methods = useForm<EquipmentTypeFormHandler>({ resolver: yupResolver(SDCountiesSchema) })
	const {
		handleSubmit,
		reset,
		formState: { isSubmitted, isDirty, isSubmitting },
	} = methods
	const sidebarSDCounties = useMemo(() => {
		return (SDCountiessList ?? [])?.map(SDCounties => ({
			_id: SDCounties._id,
			name: SDCounties.name,
		}))
	}, [saving, SDCountiessList?.length, isSubmitted])

	const onSubmit: SubmitHandler<EquipmentTypeFormHandler> = data => {
		isNewBeingCreated.current = true
		dispatch(
			saveSDCounties(data, id => {
				setSearchParams({ selected: id })
				reset(data)
			}),
		)
	}

	useEffect(() => {
		dispatch(getSDCounties())
	}, [])

	useEffect(() => {
		if (!selectedSDCounty && !!SDCountiessList?.length && !isNewBeingCreated.current) {
			setSearchParams({ selected: SDCountiessList?.[0]?._id })
		}
	}, [params, SDCountiessList])

	useEffect(() => {
		if (selectedSDCounty && SDCounties) {
			reset({ ...SDCountiesInitialValues, ...SDCounties })
		} else if (!selectedSDCounty && !isNewBeingCreated.current) {
			reset({ ...SDCountiesInitialValues })
		}
	}, [selectedSDCounty, SDCounties])

	const handleSelected = (SDCountiesId: string): void => {
		setSearchParams({ selected: SDCountiesId })
	}

	return (
		<>
			<GlobalSidebar>
				<SidebarList
					title={"Counties"}
					loading={loading}
					selected={selectedSDCounty}
					setSelected={handleSelected}
					list={sidebarSDCounties.sort(sortByProperty("name"))}
				/>
			</GlobalSidebar>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="flex w-1/4 flex-col gap-y-2">
						<FormInputAntd type="text" label="County Name" name="name" />
						<div className="flex gap-x-2">
							<AntdButton
								onClick={() => {
									isNewBeingCreated.current = true
									reset({ ...SDCountiesInitialValues })
									setSearchParams({ selected: "" })
								}}
							>
								New
							</AntdButton>
							<AntdButton disabled={!isDirty || isSubmitting} loading={saving} htmlType="submit">
								{selectedSDCounty ? "Update" : "Save"}
							</AntdButton>
						</div>
					</div>
				</form>
			</FormProvider>
		</>
	)
}

export default SDCounties
